import {
  ArrayField,
  ArrayFieldApi,
  FormApi,
  useArrayFieldItemApi,
  useArrayFieldItemState,
  useFormState,
} from 'informed';
import { RefObject, useEffect, useRef, useState } from 'react';
import Emoji from '../Emoji';
import moment from 'moment';
import { TyInput, TyNumberInput, TyTimeInput } from '@tymbe/ui-forms';
import { getDurationString, maxDuration } from './utils';
import { SecondaryButton } from '@tymbe/ui-primitives/*';
import { ActionIcon, FavouriteIcon, SocialIcon } from '@tymbe/ui-icons';
import Collapse from '../../../../components/Collapse';
import { ShiftItem } from './types';
import InvitationItem from './InvitationItem';
import classNames from 'classnames';
import Tooltip from 'rc-tooltip';
import { useFormValueMonitor } from './useFormValueMonitor';
import { ShiftTemplateData } from '@tymbe/schema/shift-template.interface';

interface ShiftItemProps {
  invitationsRef: React.RefObject<FormApi<any> | undefined>;
  onEdit?: (shift: ShiftItem, index: number) => void;
  getInvitations?: () => { id: string; selected: boolean }[];
  getShiftTemplate: (id?: string | number) => ShiftTemplateData | undefined;
}

const ShiftCreateItem = ({
  invitationsRef,
  onEdit,
  getInvitations = () => [],
  getShiftTemplate,
}: ShiftItemProps) => {
  const {
    values: shift,
    key,
    index,
    initialValue,
  } = useArrayFieldItemState<ShiftItem>();
  const { remove, setValue } = useArrayFieldItemApi();
  const [open, setOpen] = useState(false);
  const addInvitationRef = useRef<ArrayFieldApi['addWithInitialValue']>(null);

  const formState = useFormState();
  const [_, setInvitationFormValues] = useState<any>(null);

  useFormValueMonitor(
    invitationsRef as RefObject<FormApi<any>>,
    'persons',
    (persons) => setInvitationFormValues(persons)
  );

  useEffect(() => {
    if (formState.values) {
      console.log(formState.values);
    }
  }, [formState]);

  const addInvitations = () => {
    const invitations = getInvitations().filter(
      ({ id }) =>
        !shift.invitations?.find((inv) => String(inv.id) === String(id))
    );

    const totalInvitations = (shift.invitations?.length || 0) + invitations.length;
    if (totalInvitations > Number(shift.slots)) {
      setValue('slots', totalInvitations);
    }

    for (const invitation of invitations) {
      addInvitationRef.current?.({
        ...invitation,
        selected: false,
      });
    }
  };

  const itemsInInviteSelectorOtherThanAlreadyInvited = getInvitations().filter(
    ({ id }) => !shift?.invitations?.find((inv) => String(inv.id) === String(id))
  ).length;

  const canSubmitCurrentInvitations =
    itemsInInviteSelectorOtherThanAlreadyInvited !== 0;

  return (
    <div
      key={key}
      onClick={(e) => {
        // if interactive element
        if (
          e.target instanceof HTMLElement &&
          !e.target.closest('button, input, select, textarea')
        ) {
          if (shift?.invitations?.length && shift.invitations.length > 0) {
            setOpen(!open);
          }
        }
      }}
      className={classNames(
        'flex flex-col border-[1px] border-secondary-transparent-10 rounded-2xl px-4 py-2 gap-4 md:h-fit',
        { 'bg-secondary-transparent-10': open }
      )}
    >
      {/* shift emoji and date name */}
      <div className="flex items-center gap-3 h-[56px]">
        <Emoji emoji="🐛" className="size-8 bg-secondary-50" />
        <div>
          <div className="text-secondary-600">
            {moment(shift?.date).format('L')}
          </div>
          <div className="text-secondary-500 font-normal">
            {getShiftTemplate(shift?.templateId)?.template_name}
          </div>
        </div>
      </div>

      {/* start and end time */}
      <div className="flex gap-2 items-center text-secondary-500">
        <TyTimeInput
          simple
          className="w-14"
          name="startTime"
          validateWhen={['endTime']}
          validate={maxDuration}
          validateOn="change"
        />
        -
        <TyTimeInput
          simple
          className="w-14"
          name="endTime"
          validateWhen={['startTime']}
          validate={maxDuration}
          validateOn="change"
        />
        {getDurationString(shift?.startTime, shift?.endTime)}
      </div>

      {/* number of tymbers input */}
      <TyNumberInput
        name="slots"
        className="text-secondary-500 w-full"
        min={shift?.invitations?.length ?? 0}
        validate={(value) =>
          Number(value) < Number(shift?.invitations?.length || 0)
            ? 'Snažíte se pozvat více brigádníků než je volných míst.'
            : undefined
        }
      />

      {/* CR what is this for? */}
      {/* <div>{shift?.invitations?.length ?? 0}</div> */}

      {/* action buttons */}
      <div className="flex items-center gap-2">
        <Tooltip
          placement="top"
          trigger={['hover']}
          overlay="Nejprve si v levém panelu vytvořte seznam tymberů"
          overlayClassName="max-w-[200px]"
        >
          <div className="flex-grow">
            <SecondaryButton
              variant="outlined"
              className={classNames(
                !canSubmitCurrentInvitations || itemsInInviteSelectorOtherThanAlreadyInvited === 0
                  ? null
                  : 'text-white !bg-secondary-600 p-2',
                'gap-2 w-full h-[42px]'
              )}
              onClick={() => addInvitations()}
              disabled={!canSubmitCurrentInvitations || itemsInInviteSelectorOtherThanAlreadyInvited === 0}
              type="button"
            >
              <SocialIcon name="person_add" className="size-4" />
              <span className="text-body-2-md font-semibold">
                Pozvat ({itemsInInviteSelectorOtherThanAlreadyInvited})
              </span>
            </SecondaryButton>
          </div>
        </Tooltip>

        <SecondaryButton
          variant="outlined"
          className="border border-secondary-300 !p-2"
          onClick={() => onEdit?.({ ...initialValue, ...shift }, index)}
          type="button"
        >
          <FavouriteIcon name="mode_edit" className="size-5" />
        </SecondaryButton>
        <SecondaryButton
          variant="outlined-error"
          className="border border-red-300 text-red-500 !p-2"
          onClick={() => remove()}
          type="button"
        >
          <ActionIcon name="delete_outline" className="size-5" />
        </SecondaryButton>
      </div>

      {/* invited user profiles section */}
      <Collapse
        open={open}
        className="col-start-3 col-span-3 flex flex-col *:divide-y *:divide-secondary-100"
      >
        <ArrayField name="invitations">
          {({ addWithInitialValue }) => {
            addInvitationRef.current = addWithInitialValue;
            return (
              <ArrayField.Items>
                {(props) => {
                  return <InvitationItem />;
                }}
              </ArrayField.Items>
            );
          }}
        </ArrayField>
      </Collapse>
      {/* to keep in informed values */}
      <TyInput name="templateId" className="hidden" />
      <TyInput name="date" className="hidden" />
      <TyInput name="contact" className="hidden" />
      <TyInput name="requirements" className="hidden" />
      <TyInput name="utilities" className="hidden" />
      <TyInput name="noPreferences" className="hidden" />
      <TyInput name="menOnly" className="hidden" />
      <TyInput name="womenOnly" className="hidden" />
      <TyInput name="men" className="hidden" />
      <TyInput name="women" className="hidden" />
      <TyInput name="contestStart" className="hidden" />
      <TyInput name="contestEndVariant" className="hidden" />
      <TyInput name="contestEndTime" className="hidden" />
    </div>
  );
};

export default ShiftCreateItem;
